/**
 * @fileoverview Greetings Editor component with two-panel layout.
 * Left sidebar for greeting list, right panel for editor.
 * @module components/editor/GreetingsEditor
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Plus, Trash2, MessageSquare, ChevronLeft, Copy, ChevronUp, ChevronDown } from 'lucide-react';
import type {
  SamplerSettings,
  AIConfig,
  PromptSettings,
  PromptModelMap,
} from '../../db/characterTypes';
import type { CharacterSection } from '../../db/characterTypes';
import { useAIEditor } from '../../hooks';
import { estimateTokens } from '../../services/AIService';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { ConfirmDialog } from '../ui/ConfirmDialog';

interface GreetingsEditorProps {
  greetings: string[];
  onChange: (greetings: string[]) => void;
  /** Greeting selected when the editor mounts. */
  initialIndex?: number;
  onSelectedIndexChange?: (index: number) => void;
  /** Select this greeting (0-based) when nonce changes. */
  focusGreeting?: { index: number; nonce: number } | null;
  selectedText: string;
  setSelectedText: (text: string) => void;
  contextSectionIds: CharacterSection[];
  aiConfig: AIConfig;
  samplerSettings: SamplerSettings;
  promptSettings: PromptSettings;
  promptModels?: PromptModelMap;
  getContextContent: (sectionIds: CharacterSection[]) => string[] | Promise<string[]>;
  activeSection: string;
  fontSize?: number;
  onFontSizeChange?: (size: number) => void;
  spellcheck?: import('../../db/characterTypes').SpellcheckSettings;
  markdownImageOpenLinks?: boolean;
  roleplayHighlight?: import('../../db/characterTypes').RoleplayHighlightSettings;
}

interface GreetingListItemProps {
  greeting: string;
  index: number;
  tokenCount: number;
  isSelected: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

interface GreetingDetailProps {
  greeting: string;
  onPersistUpdate: (value: string) => void;
  aiConfig: AIConfig;
  samplerSettings: SamplerSettings;
  promptSettings: PromptSettings;
  promptModels?: PromptModelMap;
  getContextContent: (sectionIds: CharacterSection[]) => string[] | Promise<string[]>;
  contextSectionIds: CharacterSection[];
  setSelectedText: (text: string) => void;
  fontSize?: number;
  onFontSizeChange?: (size: number) => void;
  spellcheck?: import('../../db/characterTypes').SpellcheckSettings;
  markdownImageOpenLinks?: boolean;
  roleplayHighlight?: import('../../db/characterTypes').RoleplayHighlightSettings;
}

/**
 * Compact greeting card for the sidebar list
 */
function GreetingListItem({
  greeting,
  index,
  tokenCount,
  isSelected,
  canMoveUp,
  canMoveDown,
  onSelect,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
}: GreetingListItemProps): React.ReactElement {
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };
  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDuplicate();
  };
  const handleMoveUp = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMoveUp();
  };
  const handleMoveDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMoveDown();
  };

  const hasContent = greeting.trim().length > 0;
  const preview = greeting.replace(/\s+/g, ' ').trim();

  return (
    <div
      onClick={onSelect}
      className={`
        relative cursor-pointer rounded-xl border p-3 transition-colors touch-manipulation
        ${isSelected
          ? 'border-accent bg-accent-soft ring-1 ring-accent'
          : 'border-border bg-surface hover:border-accent/40 hover:bg-accent-soft/60'
        }
      `}
    >
      <div className="flex items-start gap-2.5">
        <div className="mt-1.5 shrink-0">
          {hasContent ? (
            <div className="h-2 w-2 rounded-full bg-success" title="Has content" />
          ) : (
            <div className="h-2 w-2 rounded-full bg-fg-subtle" title="Empty" />
          )}
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          aria-current={isSelected ? 'true' : undefined}
          className="min-w-0 flex-1 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
        >
          <span className="block text-sm font-medium text-fg">
            Greeting {index + 1}
          </span>
          <span className="mt-0.5 block text-xs text-fg-muted">
            {hasContent ? `${tokenCount.toLocaleString()} tokens` : 'Empty'}
          </span>
          {hasContent && (
            <span className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-fg-subtle">
              {preview}
            </span>
          )}
        </button>

        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={handleMoveUp}
            disabled={!canMoveUp}
            className="rounded-lg p-1.5 text-fg-muted transition-colors hover:bg-hover hover:text-fg disabled:cursor-not-allowed disabled:opacity-30 touch-manipulation"
            title="Move up"
            aria-label={`Move greeting ${index + 1} up`}
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleMoveDown}
            disabled={!canMoveDown}
            className="rounded-lg p-1.5 text-fg-muted transition-colors hover:bg-hover hover:text-fg disabled:cursor-not-allowed disabled:opacity-30 touch-manipulation"
            title="Move down"
            aria-label={`Move greeting ${index + 1} down`}
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleDuplicate}
            className="rounded-lg p-2 text-fg-muted transition-colors hover:bg-accent-soft hover:text-accent touch-manipulation"
            title="Duplicate greeting"
            aria-label={`Duplicate greeting ${index + 1}`}
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="shrink-0 rounded-lg p-2 text-fg-muted transition-colors hover:bg-danger-soft hover:text-danger touch-manipulation"
            title="Delete greeting"
            aria-label={`Delete greeting ${index + 1}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Full editor for a greeting (right panel)
 */
function GreetingDetail({
  greeting,
  onPersistUpdate,
  aiConfig,
  samplerSettings,
  promptSettings,
  promptModels,
  getContextContent,
  contextSectionIds,
  setSelectedText,
  fontSize,
  onFontSizeChange,
  spellcheck,
  markdownImageOpenLinks,
  roleplayHighlight,
}: GreetingDetailProps): React.ReactElement {
  const [draftGreeting, setDraftGreeting] = useState(greeting);

  useEffect(() => {
    setDraftGreeting(greeting);
  }, [greeting]);

  const { editorRef, payloadPreviewModal } = useAIEditor({
    value: draftGreeting,
    placeholder: 'Write greeting...',
    onImmediateChange: setDraftGreeting,
    onPersistChange: onPersistUpdate,
    saveMode: 'debounced',
    saveDebounceMs: 250,
    setSelectedText,
    aiConfig,
    samplerSettings,
    promptSettings,
    promptModels,
    getContextContent,
    contextSectionIds,
    minHeight: '200px',
    maxHeight: 'none',
    isActive: true,
    fontSize,
    onFontSizeChange,
    spellcheck,
    markdownImageOpenLinks,
    roleplayHighlight,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={editorRef}
        className="min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-bg shadow-inner"
        style={{ minHeight: '12rem' }}
      />
      {payloadPreviewModal}
    </div>
  );
}

/**
 * Greetings Editor with two-panel layout
 * Sidebar list on left, detail editor on right
 */
export function GreetingsEditor({
  greetings,
  onChange,
  initialIndex = 0,
  onSelectedIndexChange,
  focusGreeting,
  setSelectedText,
  contextSectionIds,
  aiConfig,
  samplerSettings,
  promptSettings,
  promptModels,
  getContextContent,
  fontSize,
  onFontSizeChange,
  spellcheck,
  markdownImageOpenLinks,
  roleplayHighlight,
}: GreetingsEditorProps): React.ReactElement {
  const [greetingsList, setGreetingsList] = useState<string[]>(greetings);
  const [selectedGreetingIndex, setSelectedGreetingIndex] = useState<number>(initialIndex);
  const [isMobileViewOpen, setIsMobileViewOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
  const [pendingDuplicateIndex, setPendingDuplicateIndex] = useState<number | null>(null);
  const [detailKey, setDetailKey] = useState(0);

  // Showing a different greeting remounts the editor so undo history can't carry text across greetings.
  const showOtherGreeting = useCallback((index: number) => {
    setSelectedGreetingIndex(index);
    setDetailKey((key) => key + 1);
  }, []);

  // Apply each jump once, during render, so the editor mounts on the right greeting.
  const [handledFocusNonce, setHandledFocusNonce] = useState<number | null>(null);
  if (focusGreeting && focusGreeting.nonce !== handledFocusNonce) {
    setHandledFocusNonce(focusGreeting.nonce);
    showOtherGreeting(Math.max(0, Math.min(focusGreeting.index, greetingsList.length - 1)));
    setIsMobileViewOpen(true);
  }

  // Sync list from persisted state
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setGreetingsList(greetings);
      // Reset selection if out of bounds
      setSelectedGreetingIndex(prev =>
        prev >= (greetings.length || 0) ? 0 : prev
      );
      // External list changed while a delete dialog was open — cancel it so the
      // stored index can't resolve to a different greeting.
      setPendingDeleteIndex(null);
      setPendingDuplicateIndex(null);
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [greetings]);

  // Handle greeting update (persist to parent)
  const handleGreetingPersistUpdate = useCallback((index: number, value: string) => {
    const newList = [...greetingsList];
    newList[index] = value;
    setGreetingsList(newList);
    onChange(newList);
  }, [greetingsList, onChange]);

  // Handle add greeting
  const handleAddGreeting = useCallback(() => {
    const newList = [...greetingsList, ''];
    const newIndex = newList.length - 1;
    setGreetingsList(newList);
    showOtherGreeting(newIndex);
    setIsMobileViewOpen(true);
    onChange(newList);
  }, [greetingsList, onChange, showOtherGreeting]);

  // Handle duplicate greeting
  const handleDuplicateGreeting = useCallback((index: number) => {
    setPendingDuplicateIndex(index);
  }, []);

  const handleConfirmDuplicateGreeting = useCallback(() => {
    if (pendingDuplicateIndex === null) return;
    const index = pendingDuplicateIndex;
    setPendingDuplicateIndex(null);
    if (index < 0 || index >= greetingsList.length) return;
    const newList = [...greetingsList];
    newList.splice(index + 1, 0, greetingsList[index]);
    setGreetingsList(newList);
    showOtherGreeting(index + 1);
    setIsMobileViewOpen(true);
    onChange(newList);
  }, [greetingsList, onChange, pendingDuplicateIndex, showOtherGreeting]);

  // Handle move greeting up/down
  const handleMoveGreeting = useCallback((index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= greetingsList.length) return;
    const newList = [...greetingsList];
    [newList[index], newList[target]] = [newList[target], newList[index]];
    setGreetingsList(newList);
    if (index === selectedGreetingIndex) {
      setSelectedGreetingIndex(target);
    } else {
      showOtherGreeting(target);
    }
    onChange(newList);
  }, [greetingsList, onChange, selectedGreetingIndex, showOtherGreeting]);

  // Handle delete greeting
  const handleDeleteGreeting = useCallback((index: number) => {
    setPendingDeleteIndex(index);
  }, []);

  const handleConfirmDeleteGreeting = useCallback(() => {
    if (pendingDeleteIndex === null) return;
    const index = pendingDeleteIndex;
    setPendingDeleteIndex(null);

    const newList = greetingsList.filter((_, i) => i !== index);
    setGreetingsList(newList);
    onChange(newList);

    // Adjust selected index if needed
    if (selectedGreetingIndex >= newList.length) {
      setSelectedGreetingIndex(Math.max(0, newList.length - 1));
    } else if (selectedGreetingIndex > index) {
      setSelectedGreetingIndex(selectedGreetingIndex - 1);
    }
    if (selectedGreetingIndex === index) {
      setDetailKey((key) => key + 1);
    }
  }, [greetingsList, selectedGreetingIndex, onChange, pendingDeleteIndex]);

  // Handle select greeting with mobile view
  const handleSelectGreeting = useCallback((index: number) => {
    if (index !== selectedGreetingIndex) {
      showOtherGreeting(index);
    }
    setIsMobileViewOpen(true);
  }, [selectedGreetingIndex, showOtherGreeting]);

  // Handle back to list on mobile
  const handleBackToList = useCallback(() => {
    setIsMobileViewOpen(false);
  }, []);

  // Ensure selected index is valid
  const safeSelectedIndex = selectedGreetingIndex < greetingsList.length ? selectedGreetingIndex : 0;
  const selectedGreeting = greetingsList[safeSelectedIndex];

  useEffect(() => {
    onSelectedIndexChange?.(safeSelectedIndex);
  }, [onSelectedIndexChange, safeSelectedIndex]);
  const tokenCounts = useMemo(() => greetingsList.map((greeting) => estimateTokens(greeting)), [greetingsList]);
  const totalTokens = useMemo(() => tokenCounts.reduce((sum, count) => sum + count, 0), [tokenCounts]);
  const selectedGreetingTokenCount = tokenCounts[safeSelectedIndex] ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden md:flex-row">
      {/* List panel: full height on mobile; fixed-width sidebar on desktop */}
      <div
        className={`
          flex min-h-0 w-full flex-col overflow-hidden border-border bg-muted/40
          md:w-72 md:shrink-0 md:border-r
          ${isMobileViewOpen ? 'hidden md:flex' : 'flex flex-1 md:flex-none'}
        `}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-fg-muted">
              <MessageSquare className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-fg">Greetings</p>
              <p className="text-xs text-fg-muted">
                {greetingsList.length} greeting{greetingsList.length !== 1 ? 's' : ''}
                {greetingsList.length > 0 ? ` · ${totalTokens.toLocaleString()} tokens` : ''}
              </p>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
          {greetingsList.length === 0 ? (
            <div className="flex h-full min-h-40 flex-col items-center justify-center px-4 py-10 text-center text-fg-subtle">
              <MessageSquare className="mb-3 h-10 w-10 opacity-40" />
              <p className="text-sm font-medium text-fg-muted">No greetings yet</p>
              <p className="mt-1 text-xs">Add one to give your character alternate first messages.</p>
            </div>
          ) : (
            greetingsList.map((greeting, index) => (
              <GreetingListItem
                key={index}
                greeting={greeting}
                index={index}
                tokenCount={tokenCounts[index] ?? 0}
                isSelected={index === safeSelectedIndex}
                canMoveUp={index > 0}
                canMoveDown={index < greetingsList.length - 1}
                onSelect={() => handleSelectGreeting(index)}
                onDelete={() => handleDeleteGreeting(index)}
                onDuplicate={() => handleDuplicateGreeting(index)}
                onMoveUp={() => handleMoveGreeting(index, -1)}
                onMoveDown={() => handleMoveGreeting(index, 1)}
              />
            ))
          )}
        </div>

        <div className="shrink-0 border-t border-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={handleAddGreeting}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-border-strong bg-surface px-3 py-2.5 text-sm font-medium text-fg-muted transition-colors hover:border-accent/50 hover:bg-accent-soft hover:text-accent touch-manipulation"
          >
            <Plus className="h-4 w-4" />
            New Greeting
          </button>
        </div>
      </div>

      {/* Detail panel */}
      <div
        className={`
          min-h-0 flex-1 overflow-hidden bg-surface
          ${!isMobileViewOpen ? 'hidden md:flex md:flex-col' : 'flex flex-col'}
        `}
      >
        {selectedGreeting !== undefined ? (
          <div className="flex min-h-0 flex-1 flex-col p-3 sm:p-4 md:p-6 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <div className="mb-3 flex shrink-0 items-center justify-between gap-2 md:mb-4">
              <button
                type="button"
                onClick={handleBackToList}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-fg-muted transition-colors hover:bg-hover hover:text-fg md:hidden touch-manipulation"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-fg">Greeting {safeSelectedIndex + 1}</p>
                {selectedGreetingTokenCount !== null && (
                  <p className="text-xs text-fg-muted">
                    {selectedGreetingTokenCount.toLocaleString()} tokens
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleDuplicateGreeting(safeSelectedIndex)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:border-accent/40 hover:bg-accent-soft hover:text-accent touch-manipulation"
                  title="Duplicate greeting"
                >
                  <Copy className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Duplicate</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteGreeting(safeSelectedIndex)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:border-danger/40 hover:bg-danger-soft hover:text-danger touch-manipulation"
                  title="Delete greeting"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span className="sm:inline">Delete</span>
                </button>
              </div>
            </div>
            <GreetingDetail
              key={detailKey}
              greeting={selectedGreeting}
              onPersistUpdate={(value) => handleGreetingPersistUpdate(safeSelectedIndex, value)}
              aiConfig={aiConfig}
              samplerSettings={samplerSettings}
              promptSettings={promptSettings}
              promptModels={promptModels}
              getContextContent={getContextContent}
              contextSectionIds={contextSectionIds}
              setSelectedText={setSelectedText}
              fontSize={fontSize}
              onFontSizeChange={onFontSizeChange}
              spellcheck={spellcheck}
              markdownImageOpenLinks={markdownImageOpenLinks}
              roleplayHighlight={roleplayHighlight}
            />
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-fg-subtle">
            <div className="px-6 text-center">
              <MessageSquare className="mx-auto mb-3 h-12 w-12 opacity-40" />
              <p className="text-sm font-medium text-fg-muted">Select a greeting to edit</p>
              <p className="mt-1 text-xs">Or create a new one to get started</p>
            </div>
          </div>
        )}
      </div>
      <ConfirmDeleteDialog
        open={pendingDeleteIndex !== null}
        title={`Delete greeting ${(pendingDeleteIndex ?? 0) + 1}?`}
        message="This cannot be undone. The greeting will be removed immediately."
        onConfirm={handleConfirmDeleteGreeting}
        onCancel={() => setPendingDeleteIndex(null)}
      />
      <ConfirmDialog
        open={pendingDuplicateIndex !== null}
        title={`Duplicate greeting ${(pendingDuplicateIndex ?? 0) + 1}?`}
        message="A copy will be inserted right after this greeting."
        confirmLabel="Duplicate"
        onConfirm={handleConfirmDuplicateGreeting}
        onCancel={() => setPendingDuplicateIndex(null)}
      />
    </div>
  );
}

export default GreetingsEditor;
